import type { LevelTilemap, LevelDecoration, ObjectDecoration } from "@/types/tileset.types"
import { rasterizePath, buildGroundTiles } from "./autotile"
import { TILE_SIZE, GRID_WIDTH, GRID_HEIGHT } from "@/data/game-settings"

// ============================================
// LEVEL 1 - THE SEWERS (hand-placed decorations)
// ============================================
// Ground autotiling is shared with every other level (see autotile.ts).
// The path here matches levels.ts: 2 tiles (64px) wide, waypoints on tile
// boundaries via tileToPathCenter.

const P = (tileX: number, tileY: number) => ({ x: tileX * TILE_SIZE, y: tileY * TILE_SIZE })

const pathSubtiles = rasterizePath(
  [P(18, -1), P(18, 5), P(28, 5), P(28, 15), P(8, 15), P(8, 25)],
  TILE_SIZE * 2,
)

// Generate decorations - empty for level 1 (no decorations from spritesheets)
// Coordinates are in logical tiles (32px), not subtiles
function generateDecorations(): LevelDecoration[] {
  return []
}

// Object decorations - individual PNG images that mark their tiles as unbuildable
// Path layout (in tile coords):
//   Entry: x=16-19, y=0-5 | Right: x=16-29, y=4-6 | Down: x=27-29, y=4-16
//   Across: x=6-29, y=14-16 | Exit: x=6-9, y=14-25
const objectDecorations: ObjectDecoration[] = [
  // Large (4x4 = 128x128px)
  { imagePath: "/images/biomes/grassland/objects/Tree1.png", tileX: 1, tileY: 1, widthTiles: 4, heightTiles: 4, zIndex: 10 },
  { imagePath: "/images/biomes/grassland/objects/Tree2.png", tileX: 32, tileY: 2, widthTiles: 4, heightTiles: 4, zIndex: 10 },
  { imagePath: "/images/biomes/grassland/objects/Ruin1_grass_shadow.png", tileX: 22, tileY: 8, widthTiles: 4, heightTiles: 4, zIndex: 10 },
  { imagePath: "/images/biomes/grassland/objects/Tree3.png", tileX: 0, tileY: 8, widthTiles: 4, heightTiles: 4, zIndex: 10 },
  { imagePath: "/images/biomes/grassland/objects/Tree4.png", tileX: 15, tileY: 18, widthTiles: 4, heightTiles: 4, zIndex: 10 },

  // Medium (2x2 = 64x64px)
  { imagePath: "/images/biomes/grassland/objects/Bush1.png", tileX: 3, tileY: 8, widthTiles: 2, heightTiles: 2, zIndex: 8 },
  { imagePath: "/images/biomes/grassland/objects/Bush2.png", tileX: 11, tileY: 3, widthTiles: 2, heightTiles: 2, zIndex: 8 },
  { imagePath: "/images/biomes/grassland/objects/Rpck_grass1.png", tileX: 33, tileY: 10, widthTiles: 2, heightTiles: 2, zIndex: 8 },
  { imagePath: "/images/biomes/grassland/objects/Rpck_grass2.png", tileX: 2, tileY: 18, widthTiles: 2, heightTiles: 2, zIndex: 8 },
  { imagePath: "/images/biomes/grassland/objects/Stone1_grass_shadow.png", tileX: 12, tileY: 8, widthTiles: 2, heightTiles: 2, zIndex: 8 },
  { imagePath: "/images/biomes/grassland/objects/Ruin3_grass_shadow.png", tileX: 31, tileY: 17, widthTiles: 2, heightTiles: 2, zIndex: 8 },
  { imagePath: "/images/biomes/grassland/objects/Bush3.png", tileX: 20, tileY: 1, widthTiles: 2, heightTiles: 2, zIndex: 8 },
  { imagePath: "/images/biomes/grassland/objects/Bush4.png", tileX: 10, tileY: 10, widthTiles: 2, heightTiles: 2, zIndex: 8 },
  { imagePath: "/images/biomes/grassland/objects/Rpck_grass3.png", tileX: 0, tileY: 20, widthTiles: 2, heightTiles: 2, zIndex: 8 },
  { imagePath: "/images/biomes/grassland/objects/Stone2_grass_shadow.png", tileX: 34, tileY: 18, widthTiles: 2, heightTiles: 2, zIndex: 8 },

  // Small (1x1 = 32x32px)
  { imagePath: "/images/biomes/grassland/objects/Broken_tree1.png", tileX: 0, tileY: 12, widthTiles: 1, heightTiles: 1, zIndex: 6 },
  { imagePath: "/images/biomes/grassland/objects/Broken_tree2.png", tileX: 5, tileY: 6, widthTiles: 1, heightTiles: 1, zIndex: 6 },
  { imagePath: "/images/biomes/grassland/objects/Bush10.png", tileX: 34, tileY: 6, widthTiles: 1, heightTiles: 1, zIndex: 6 },
  { imagePath: "/images/biomes/grassland/objects/Bush11.png", tileX: 31, tileY: 14, widthTiles: 1, heightTiles: 1, zIndex: 6 },
  { imagePath: "/images/biomes/grassland/objects/Ruin5_grass_shadow.png", tileX: 14, tileY: 1, widthTiles: 1, heightTiles: 1, zIndex: 6 },
  { imagePath: "/images/biomes/grassland/objects/Stone3_grass_shadow.png", tileX: 24, tileY: 17, widthTiles: 1, heightTiles: 1, zIndex: 6 },
  { imagePath: "/images/biomes/grassland/objects/Stone4_grass_shadow.png", tileX: 4, tileY: 22, widthTiles: 1, heightTiles: 1, zIndex: 6 },
  { imagePath: "/images/biomes/grassland/objects/grass_element2.png", tileX: 10, tileY: 20, widthTiles: 1, heightTiles: 1, zIndex: 6 },
  { imagePath: "/images/biomes/grassland/objects/grass_element3.png", tileX: 33, tileY: 20, widthTiles: 1, heightTiles: 1, zIndex: 6 },
  { imagePath: "/images/biomes/grassland/objects/Broken_tree4.png", tileX: 15, tileY: 7, widthTiles: 1, heightTiles: 1, zIndex: 6 },
  { imagePath: "/images/biomes/grassland/objects/Broken_tree5.png", tileX: 25, tileY: 3, widthTiles: 1, heightTiles: 1, zIndex: 6 },
  { imagePath: "/images/biomes/grassland/objects/Bush12.png", tileX: 1, tileY: 16, widthTiles: 1, heightTiles: 1, zIndex: 6 },
  { imagePath: "/images/biomes/grassland/objects/Bush13.png", tileX: 20, tileY: 13, widthTiles: 1, heightTiles: 1, zIndex: 6 },
  { imagePath: "/images/biomes/grassland/objects/Rpck_grass5.png", tileX: 35, tileY: 15, widthTiles: 1, heightTiles: 1, zIndex: 6 },
]

export const level1Tilemap: LevelTilemap = {
  width: GRID_WIDTH,
  height: GRID_HEIGHT,
  tiles: buildGroundTiles(pathSubtiles),
  decorations: generateDecorations(),
  objectDecorations,
}
